import React, { useState } from 'react';
import { X, Loader2, Upload, ImageIcon, Instagram, Facebook, Save, Trophy, Cpu, Wand2, Sparkles, Crown, Clock } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { base44 } from '@/api/base44Client';

const inputCls = 'bg-[#0d0d0f] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60';

const COMPETITION_URL = 'https://motionreality.co.uk/competition';

const TEMPLATES = [
  {
    key: 'monthly_free',
    label: 'MONTHLY FREE DRAW',
    icon: Trophy,
    source_label: 'Competition — Monthly Free Draw',
    caption: `🏆 WIN A FREE SIM RACING EXPERIENCE — EVERY MONTH!\n\nEvery month we give away a FREE session on a pro-spec direct-drive rig at our Bromborough venue. One random draw. No purchase necessary.\n\n✅ Free to enter\n✅ Open to UK residents aged 5+\n✅ Winner drawn monthly & notified by email\n\nEnter in seconds at the link below 👇\n\n#SimRacing #MotionReality #Competition #FreeEntry #Win #RaceSim #SimRacingUK`,
  },
  {
    key: 'sim_rig',
    label: 'WIN A SIM RIG · £5',
    icon: Cpu,
    source_label: 'Competition — Win a Sim Rig',
    caption: `🏎️ WIN A FULL SIM RACING RIG FOR JUST £5!\n\nEvery six months, one lucky winner takes home a complete pro-spec sim rig — direct-drive wheelbase, load-cell pedals, cockpit & triple-screen setup. £5 per entry, enter as many times as you like.\n\n✅ £5 per entry — unlimited entries\n✅ Open to UK residents aged 18+\n✅ Drawn at random at the end of each half-year\n\nGrab your entries now 👇\n\n#SimRacing #MotionReality #WinASimRig #Competition #SimRig #RaceSim #Giveaway`,
  },
  {
    key: 'last_chance',
    label: 'LAST CHANCE',
    icon: Clock,
    source_label: 'Competition — Last Chance',
    caption: `⏰ LAST CHANCE TO ENTER!\n\nOur free monthly sim racing experience draw closes soon. Don't miss your chance to win a session on a pro-spec rig at our Bromborough venue.\n\nIt's free, it takes 30 seconds, and you could be our next winner. 🏆\n\nEnter now 👇\n\n#SimRacing #MotionReality #Competition #LastChance #FreeEntry #Win`,
  },
  {
    key: 'winner',
    label: 'WINNER ANNOUNCE',
    icon: Crown,
    source_label: 'Competition — Winner Announcement',
    caption: `🏆 WE HAVE A WINNER! 🏆\n\nCongratulations to our latest competition winner! You've won a free sim racing experience at our Bromborough venue. 🎉\n\nWant to be next month's winner? Enter free every month — one random draw, open to UK residents aged 5+.\n\nEnter now 👇\n\n#SimRacing #MotionReality #Competition #Winner #Win #RaceSim`,
  },
];

export default function CreateCompetitionPostModal({ onClose, onCreated }) {
  const [templateKey, setTemplateKey] = useState('monthly_free');
  const [caption, setCaption] = useState(TEMPLATES[0].caption);
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState(COMPETITION_URL);
  const [ig, setIg] = useState(true);
  const [fb, setFb] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const [genCaption, setGenCaption] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const applyTemplate = (key) => {
    const t = TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    setTemplateKey(key);
    setCaption(t.caption);
    setLinkUrl(COMPETITION_URL);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setImageUrl(file_url);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateImage = async () => {
    setError('');
    setGenImg(true);
    try {
      const t = TEMPLATES.find((x) => x.key === templateKey);
      const prompt = `Bold promotional social media graphic for a sim racing competition. Dark charcoal background with red and white racing accents, a gleaming trophy and a racing steering wheel, dramatic lighting, high contrast, space for text, premium motorsport aesthetic. Square Instagram format.`;
      const res = await base44.integrations.Core.GenerateImage({ prompt });
      const url = res?.url;
      if (url) setImageUrl(url);
      else setError('AI did not return an image. Try again.');
    } catch (err) {
      setError(err.message || 'Image generation failed.');
    } finally {
      setGenImg(false);
    }
  };

  const handleAiCaption = async () => {
    setError('');
    setGenCaption(true);
    try {
      const t = TEMPLATES.find((x) => x.key === templateKey);
      const prompt = `You are a social media copywriter for Motion Reality, a premium sim racing hardware store and cockpit booking venue in Bromborough (near Liverpool), UK. Write a MEGA, high-impact promotional social media caption for Instagram & Facebook promoting our "${t.label}" competition. It should be energetic, premium, and persuasive — 2-4 short paragraphs with line breaks, emojis, clear benefits, and a strong call to action to enter at motionreality.co.uk/competition. End with hashtags including #SimRacing #MotionReality #Competition. Return only the caption text — no quotes, no preamble.`;
      const res = await base44.integrations.Core.InvokeLLM({
        prompt,
        response_json_schema: { type: 'object', properties: { caption: { type: 'string' } } },
      });
      const generated = (res && typeof res === 'object' ? res.caption : '') || (typeof res === 'string' ? res : '');
      if (generated) setCaption(generated.trim());
      else setError('AI did not return a caption. Try again.');
    } catch (err) {
      setError(err.message || 'AI generation failed.');
    } finally {
      setGenCaption(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!caption.trim()) {
      setError('Caption is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const t = TEMPLATES.find((x) => x.key === templateKey);
      await base44.entities.SocialPost.create({
        source_type: 'competition',
        source_label: t.source_label,
        caption: caption.trim(),
        image_url: imageUrl,
        link_url: linkUrl.trim(),
        post_to_instagram: ig,
        post_to_facebook: fb,
        status: 'draft',
        created_by_name: 'Admin',
      });
      onCreated();
    } catch (err) {
      setError(err.message || 'Failed to create post.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-primary/30 w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111115] z-10">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            <p className="text-primary text-[10px] font-bold tracking-widest">MEGA COMPETITION POST</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
          {/* Template picker */}
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">COMPETITION TEMPLATE</p>
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                const active = templateKey === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => applyTemplate(t.key)}
                    className={`flex items-center gap-2 px-3 py-2.5 border text-[10px] font-bold tracking-widest transition-colors text-left ${
                      active ? 'border-primary text-white bg-primary/10' : 'border-white/10 text-white/50 hover:text-white hover:border-white/30'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-primary' : 'text-white/40'}`} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Image */}
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">IMAGE</p>
            <div className="flex gap-3">
              <div className="w-20 h-20 bg-[#0d0d0f] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-white/20" />}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex gap-2 flex-wrap">
                  <label className="cursor-pointer flex items-center gap-1.5 bg-[#0d0d0f] border border-white/10 hover:border-white/30 text-white/60 hover:text-white text-[10px] font-bold tracking-wider px-3 py-2 transition-colors">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    UPLOAD
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                  </label>
                  <button
                    type="button"
                    onClick={handleGenerateImage}
                    disabled={genImg}
                    className="flex items-center gap-1.5 bg-[#0d0d0f] border border-primary/30 text-primary hover:bg-primary/10 text-[10px] font-bold tracking-wider px-3 py-2 transition-colors disabled:opacity-50"
                  >
                    {genImg ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {genImg ? 'GENERATING…' : 'AI GRAPHIC'}
                  </button>
                </div>
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={`${inputCls} text-[11px] h-8`} placeholder="or paste image URL" />
              </div>
            </div>
            {ig && !imageUrl && (
              <p className="text-amber-400/70 text-[10px] mt-1.5 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" /> Instagram requires an image — IG will be skipped if left blank.
              </p>
            )}
          </div>

          {/* AI caption */}
          <div className="flex items-center justify-between">
            <p className="text-white/40 text-[10px] font-bold tracking-widest">CAPTION</p>
            <button
              type="button"
              onClick={handleAiCaption}
              disabled={genCaption}
              className="flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-3 py-1.5 border border-primary/40 text-primary bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50"
            >
              {genCaption ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
              {genCaption ? 'GENERATING…' : 'AI REWRITE (MEGA)'}
            </button>
          </div>
          <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={8} className="bg-[#0d0d0f] border-white/10 text-white text-sm resize-y" />

          {/* Link */}
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">LINK URL</p>
            <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className={inputCls} placeholder="https://..." />
          </div>

          {/* Platform toggles */}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setIg(!ig)}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 border transition-colors ${ig ? 'text-primary border-primary/40 bg-primary/10' : 'text-white/30 border-white/10 hover:border-white/30'}`}
            >
              <Instagram className="w-3.5 h-3.5" /> INSTAGRAM {ig ? 'ON' : 'OFF'}
            </button>
            <button
              type="button"
              onClick={() => setFb(!fb)}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 border transition-colors ${fb ? 'text-blue-400 border-blue-400/40 bg-blue-400/10' : 'text-white/30 border-white/10 hover:border-white/30'}`}
            >
              <Facebook className="w-3.5 h-3.5" /> FACEBOOK {fb ? 'ON' : 'OFF'}
            </button>
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex gap-3 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="flex-1 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-xs font-bold tracking-wider py-3 transition-colors">
              CANCEL
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'SAVING…' : 'CREATE DRAFT'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}